import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Declare the BASE_URL at the top for API and document downloads
const BASE_URL = 'https://oxfordstudycenterapp.site';

const AssessmentResults = () => {
  const { assessmentId } = useParams(); // Get the assessment ID from the URL
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch assessment info and results
  const fetchAssessmentResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/assessments-results/get-assessment-results/${assessmentId}`);
      const { assessmentInfo, studentResults } = response.data;
      
      setAssessmentInfo(assessmentInfo);
      setStudentResults(studentResults);
    } catch (error) {
      console.error('Error fetching assessment results:', error);
      toast.error('Failed to load assessment results.');
    } finally {
      setLoading(false);
    }
  };

  // Function to download the document
  const handleDownloadDocument = (document) => {
    if (document) {
      const downloadUrl = `${BASE_URL}/assessments/download-document/${document}`; // Combine BASE_URL with document name
      window.open(downloadUrl, '_blank');
    } else {
      toast.error('No document available for download.');
    }
  };

  useEffect(() => {
    fetchAssessmentResults();
  }, [assessmentId]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Assessment Results</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {/* Loading Indicator */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="relative">
                <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
                <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
              </div>
            </div>
          )}

          {/* Assessment Info */}
          {assessmentInfo && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">{assessmentInfo.AssessmentName}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-2"><span className="font-medium text-gray-700">Created On:</span> {new Date(assessmentInfo.DateCreated).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600 mb-2"><span className="font-medium text-gray-700">Due Date:</span> {new Date(assessmentInfo.DateDue).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2"><span className="font-medium text-gray-700">Class:</span> {assessmentInfo.ClassName} (Form {assessmentInfo.Form}, Term {assessmentInfo.Term}, {assessmentInfo.Year})</p>
                  <p className="text-sm text-gray-600 mb-2"><span className="font-medium text-gray-700">Teacher:</span> {`${assessmentInfo.EmployeeName} ${assessmentInfo.EmployeeSurname}`}</p>
                </div>
              </div>

              {/* Download Document Button */}
              {assessmentInfo.Document && (
                <button
                  onClick={() => handleDownloadDocument(assessmentInfo.Document)}
                  className="mt-6 bg-pink-600 text-white px-6 py-2 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors"
                >
                  Download Document
                </button>
              )}
            </div>
          )}

          {/* No Assessment Info Found */}
          {!loading && !assessmentInfo && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No assessment information found.</p>
            </div>
          )}

          {/* Student Results */}
          {studentResults.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-pink-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reg Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Student Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Mark</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {studentResults.map(result => (
                    <tr key={result.resultId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.regNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{`${result.studentName} ${result.studentSurname}`}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{result.mark}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No results available for this assessment.</p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default AssessmentResults;
